<template>
  <div
    class="rbz-table-wrapper"
    :data-qa="dataQa"
    :style="maxTableHeight ? `max-height: ${maxTableHeight}` : ''"
    ref="tableRef"
  >
    <div
      class="top-navbar"
      ref="topNavbarRef"
      data-qa="table-top-navbar"
      :style="{
        position: stickyTopNavbar ? 'sticky' : 'static',
        top: '0',
      }"
      v-if="showTopNavbar"
    >
      <div class="table-results-wrapper">
        <template v-if="$slots.tableResultsOverride">
          <slot name="tableResultsOverride" :numberOfResults="dataArrayDisplay.length || 0" />
        </template>
        <template v-else>
          <span class="table-results rbz-text-regular" v-if="showTableResults">
            {{ numberOfResultsString }}
          </span>
        </template>
      </div>
      <div class="table-buttons align-items-center">
        <div class="table-buttons-icons">
          <form-button
            v-if="showDeleteSelectedButton && selectedArray.length > 0"
            variant="tertiary"
            icon="IconDelete"
            class="delete-table-data-button"
            data-qa="delete-table-data-button"
            @button-click="deleteData"
            v-b-tooltip="t('table.deleteSelectedTooltip')"
          />
          <form-button
            v-if="showDownloadButton"
            variant="tertiary"
            icon="IconDownload"
            class="download-table-data-button"
            data-qa="download-table-data-button"
            @button-click="downloadData"
            v-b-tooltip="t('table.downloadTooltip')"
          />
          <form-button
            v-if="showFilterButton"
            variant="tertiary"
            icon="IconFilter"
            :icon-props="{ active: filtersVisible }"
            class="filter-table-data-button"
            data-qa="filter-table-data-button"
            @button-click="filtersVisible = !filtersVisible"
            :ignore-update-in-process="true"
            v-b-tooltip="t('table.filterTooltip')"
          />
        </div>
        <div
          class="vertical-divider"
          v-if="
            ($slots.tableButtonsRightOverride || showNewButton) &&
            ((showCheckboxColumn && selectedArray.length > 0) ||
              showDownloadButton ||
              showFilterButton)
          "
        >
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <template v-if="$slots.tableButtonsRightOverride">
          <slot name="tableButtonsRightOverride" />
        </template>
        <template v-else>
          <form-button
            v-if="showNewButton"
            :label="newButtonLabel || t('table.newButtonLabel')"
            :link="newButtonLink ? newButtonLink : null"
            :classes="newButtonClasses"
            variant="secondary"
            icon="IconAdd"
            class="new-button"
            data-qa="new-table-entry-button"
            @button-click="newButtonClicked()"
          />
        </template>
      </div>
    </div>
    <table class="rbz-table" data-qa="table" :class="{ 'filters-active': filtersVisible }">
      <colgroup>
        <col v-if="showExpanderColumn" class="expander-cell" />
        <col v-if="showCheckboxColumn" class="checkbox-cell" />
        <col
          v-if="
            !showExpanderColumn &&
            !showCheckboxColumn &&
            showFilterButton &&
            showTopNavbar &&
            showNarrowColumn
          "
          class="narrow-cell filter-icon-placeholder-cell"
        />
        <col v-for="col in columns" :key="col.title" :class="[col.classes]" />
        <col
          class="row-buttons-cell"
          :style="`--buttons-count:${buttonsCount}`"
          v-if="showButtonsColumn"
        />
      </colgroup>
      <thead
        class="rbz-table-head"
        data-qa="table-head"
        :style="stickyHeader ? { position: 'sticky', top: tableHeaderStickyTop } : {}"
      >
        <tr v-if="!hideHeaderRow" class="header-row">
          <th v-if="showExpanderColumn" class="expander-cell"></th>
          <th v-if="showCheckboxColumn" class="checkbox-cell">
            <div>
              <form-checkbox
                id="checkbox-header"
                data-qa="all-checkbox"
                :checked="
                  selectedArray.length === slicedDataArrayDisplay.length &&
                  !!slicedDataArrayDisplay.length
                "
                @update:checkedValue="selectAll"
              />
            </div>
          </th>
          <th
            v-if="
              !showExpanderColumn &&
              !showCheckboxColumn &&
              showFilterButton &&
              showTopNavbar &&
              showNarrowColumn
            "
            class="narrow-cell filter-icon-placeholder-cell"
          ></th>
          <th
            v-for="(col, index) in columns"
            :key="col.title"
            class="header-cell column-title"
            :data-qa="`header-cell-${col.id}`"
            :class="[{ 'is-clickable': col?.isSortable }, col.classes]"
            @click="sortColumn(col)"
          >
            <div class="header-cell-content">
              <slot v-if="col.headerSlotName" :name="col.headerSlotName" />
              <div v-else-if="!col.hideTitle">
                {{ col.title }}
                <span v-if="numberOfResultsInColumn === index">
                  ({{ numberOfResultsString }})
                </span>
              </div>
              <icon-sort-arrow
                v-if="col?.isSortable && sortColumnTitle === col.title"
                color="shuttle-gray"
                data-qa="sort-arrow"
                :direction="sortDirection === 'asc' ? 'up' : 'down'"
              />
            </div>
          </th>
          <th
            class="header-cell row-buttons-cell"
            :style="`--buttons-count:${buttonsCount}`"
            v-if="showButtonsColumn"
          >
            <slot name="actionsColumnHeader" />
          </th>
        </tr>
        <tr v-if="filtersVisible" class="filters-row">
          <td
            v-if="showNarrowColumn"
            class="filter-icon-cell"
            :colspan="Math.max(1, (showExpanderColumn ? 1 : 0) + (showCheckboxColumn ? 1 : 0))"
          >
            <div class="filter-icon-wrapper">
              <icon-filter color="shuttle-gray" />
            </div>
          </td>
          <td
            v-for="col in columns"
            :key="col.title"
            class="filter-cell"
            :data-qa="`filter-cell-${col.id}`"
            :class="col.classes"
          >
            <div v-if="col.isSearchable">
              <form-input
                name="filter-input"
                class="filter-input"
                data-qa="filter-input"
                v-model:inputValue="filter[col.id]"
                :placeholder="col.title"
                :ignore-update-in-process="true"
                :auto-focus="col.autoFocus"
                @input="updateDataArrayDisplay()"
              />
            </div>
          </td>
          <td class="filter-cell" v-if="showButtonsColumn"></td>
        </tr>
        <tr
          v-if="showNewRow && !showNewRowAtBottom"
          ref="additionRow"
          data-qa="addition-row"
          class="addition-row"
        >
          <td v-if="showExpanderColumn"></td>
          <td v-if="showCheckboxColumn"></td>
          <td
            v-if="!showExpanderColumn && !showCheckboxColumn && showFilterButton && showTopNavbar"
          ></td>
          <template v-if="$slots.newRowOverride">
            <slot name="newRowOverride" :data="newRowDataObject" :mode="`new`" />
          </template>
          <template v-else>
            <td v-for="col in columns" :key="col.title" :class="[col.classes]">
              <template v-if="col.overrideNewInput">
                <slot :name="col.overrideNewInput" :data="newRowDataObject" :mode="`new`" />
              </template>
              <template v-else>
                <form-input
                  :name="`new-row-${col.id}-input`"
                  :data-qa="`new-row-${col.id}-input`"
                  :placeholder="col.title"
                  :is-invalid="'validate' in col ? !col.validate(newRowDataObject) : false"
                  :error-message-text="
                    'errorMessage' in col ? col.errorMessage(newRowDataObject) : ''
                  "
                  v-model:inputValue="newRowDataObject[col.fieldNames[0]]"
                />
              </template>
            </td>
          </template>
          <template v-if="$slots.newRowOverrideConfirmationButtons">
            <slot name="newRowOverrideConfirmationButtons" :data="newRowDataObject" :mode="`new`" />
          </template>
          <template v-else>
            <td
              class="row-buttons-cell"
              :style="`--buttons-count:${buttonsCount}`"
              v-if="showButtonsColumn"
            >
              <form-confirmation
                @confirm="addInlineData"
                :is-invalid="isDataObjectInvalid(newRowDataObject)"
                @cancel="setShowNewRow(false)"
              />
            </td>
          </template>
        </tr>
      </thead>
      <tbody data-qa="table-body">
        <template v-for="(row, index) in slicedDataArrayDisplay" :key="row.id">
          <tr
            :class="[
              {
                'data-row': !rowsInEditMode[row.id],
                'addition-row': !!rowsInEditMode[row.id],
                'row-expanded': isCurrentExpandedOpen(row.id),
                'row-checked': isCurrentCheckboxIsChecked(row.id),
                'row-focused': isCurrentRowFocused(row.id),
                'is-clickable': showExpanderColumn,
              },
              rowClasses ? rowClasses(row) : '',
            ]"
            :data-qa="`data-row-${index}`"
            @click="
              showExpanderColumn ? toggleExpander(row.id) : null;
              emit('row-clicked', row);
            "
            @keyup.space="showExpanderColumn ? toggleExpander(row.id) : null"
            @keydown.enter="showExpanderColumn ? toggleExpander(row.id) : null"
          >
            <td v-if="showExpanderColumn" class="expander-cell" tabindex="0">
              <icon-chevron
                v-if="showExpanderColumn"
                color="shuttle-gray"
                :direction="isCurrentExpandedOpen(row.id) ? 'down' : 'right'"
              />
            </td>
            <td v-if="showCheckboxColumn" class="checkbox-cell">
              <form-checkbox
                :id="row.id"
                :checked="selectedArray.includes(row.id)"
                :ref="row.id"
                :data-qa="`data-row-${index}-checkbox`"
                @focus="focusedRowId = row.id"
                @blur="focusedRowId = null"
                @change="rowSelected(row.id)"
                @update:checkedValue="
                  (value) => {
                    rowChecked = value;
                  }
                "
              />
            </td>
            <td
              v-if="
                !showExpanderColumn &&
                !showCheckboxColumn &&
                showFilterButton &&
                showTopNavbar &&
                showNarrowColumn
              "
            ></td>
            <template v-if="!!rowsInEditMode[row.id]">
              <template v-if="$slots.newRowOverride">
                <slot name="newRowOverride" :data="rowsInEditMode[row.id]" :mode="`edit`" />
              </template>
              <template v-else>
                <td v-for="col in columns" :key="col.title" :class="[col.classes]">
                  <template v-if="col.overrideNewInput">
                    <slot
                      :name="col.overrideNewInput"
                      :data="rowsInEditMode[row.id]"
                      :mode="`edit`"
                    />
                  </template>
                  <template v-else>
                    <form-input
                      :name="`edit-row-${col.id}-input`"
                      :data-qa="`edit-row-${col.id}-input`"
                      :placeholder="col.title"
                      :is-invalid="
                        'validate' in col ? !col.validate(rowsInEditMode[row.id]) : false
                      "
                      :error-message-text="
                        'errorMessage' in col ? col.errorMessage(rowsInEditMode[row.id]) : ''
                      "
                      v-model:inputValue="rowsInEditMode[row.id][col.fieldNames[0]]"
                    />
                  </template>
                </td>
              </template>
              <template v-if="$slots.newRowOverrideConfirmationButtons">
                <slot
                  name="newRowOverrideConfirmationButtons"
                  :data="rowsInEditMode[row.id]"
                  :mode="`edit`"
                />
              </template>
              <template v-else>
                <td
                  class="row-buttons-cell"
                  :style="`--buttons-count:${buttonsCount}`"
                  v-if="showButtonsColumn"
                >
                  <form-confirmation
                    @confirm="editInlineData(rowsInEditMode[row.id])"
                    :is-invalid="isDataObjectInvalid(rowsInEditMode[row.id])"
                    @cancel="delete rowsInEditMode[row.id]"
                  />
                </td>
              </template>
            </template>
            <template v-else>
              <td
                v-for="(col, colIndex) in columns"
                :key="col.title"
                class="data-cell"
                :class="[col.classes]"
              >
                <router-link
                  v-if="col.linkCallback"
                  :to="col.linkCallback(row)"
                  @click="null"
                  class="router-link"
                  tabindex="-1"
                >
                  <div
                    class="data-cell-content"
                    v-if="col.tooltipFunction || col.tooltipFunctionAlwaysDisplayed !== undefined"
                    @contextmenu="col.onContextMenu ? col.onContextMenu($event, row) : null"
                    @click="col.onClick ? col.onClick($event, row) : null"
                    :class="cellContentClasses(col, row)"
                    :ref="(el) => registerCellRef(el, `${colIndex}-${index}`)"
                    v-b-tooltip.bottom="
                      col.tooltipFunctionAlwaysDisplayed || overflowCellMap[`${colIndex}-${index}`]
                        ? cellTooltip(col, row)
                        : ''
                    "
                  >
                    <slot
                      v-if="col.displaySlotName"
                      :name="col.displaySlotName"
                      :row="row"
                      :col="col"
                    >
                    </slot>
                    <span v-else-if="col.displayFunction">
                      {{ col.displayFunction(row) }}
                    </span>
                    <span v-else>
                      {{ row[col.fieldNames[0]] }}
                    </span>
                  </div>
                  <div
                    class="data-cell-content"
                    v-else
                    @contextmenu="col.onContextMenu ? col.onContextMenu($event, row) : null"
                    @click="col.onClick ? col.onClick($event, row) : null"
                    :class="cellContentClasses(col, row)"
                    :ref="(el) => (dataCellRefs[`${colIndex}-${index}`] = el)"
                  >
                    <slot
                      v-if="col.displaySlotName"
                      :name="col.displaySlotName"
                      :row="row"
                      :col="col"
                    >
                    </slot>
                    <span v-else-if="col.displayFunction">
                      {{ col.displayFunction(row) }}
                    </span>
                    <span v-else>
                      {{ row[col.fieldNames[0]] }}
                    </span>
                  </div>
                </router-link>
                <div v-else>
                  <div
                    v-if="col.tooltipFunction || col.tooltipFunctionAlwaysDisplayed !== undefined"
                    class="data-cell-content"
                    @contextmenu="col.onContextMenu ? col.onContextMenu($event, row) : null"
                    @click="col.onClick ? col.onClick($event, row) : null"
                    :class="cellContentClasses(col, row)"
                    :ref="(el) => registerCellRef(el, `${colIndex}-${index}`)"
                    v-b-tooltip.bottom="
                      col.tooltipFunctionAlwaysDisplayed || overflowCellMap[`${colIndex}-${index}`]
                        ? cellTooltip(col, row)
                        : ''
                    "
                  >
                    <slot
                      v-if="col.displaySlotName"
                      :name="col.displaySlotName"
                      :row="row"
                      :col="col"
                    >
                    </slot>
                    <span v-else-if="col.displayFunction">
                      {{ col.displayFunction(row) }}
                    </span>
                    <span v-else>
                      {{ row[col.fieldNames[0]] }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="data-cell-content"
                    @contextmenu="col.onContextMenu ? col.onContextMenu($event, row) : null"
                    @click="col.onClick ? col.onClick($event, row) : null"
                    :class="cellContentClasses(col, row)"
                    :ref="(el) => (dataCellRefs[`${colIndex}-${index}`] = el)"
                  >
                    <slot
                      v-if="col.displaySlotName"
                      :name="col.displaySlotName"
                      :row="row"
                      :col="col"
                    >
                    </slot>
                    <span v-else-if="col.displayFunction">
                      {{ col.displayFunction(row) }}
                    </span>
                    <span v-else>
                      {{ row[col.fieldNames[0]] }}
                    </span>
                  </div>
                </div>
              </td>
              <td
                class="row-buttons-cell"
                :style="`--buttons-count:${buttonsCount}`"
                v-if="showButtonsColumn"
              >
                <div class="row-buttons align-items-center">
                  <FormButton
                    v-for="rowButton in rowButtons"
                    :key="rowButton.id"
                    :label="rowButton.label"
                    :link="rowButton.linkCallback ? rowButton.linkCallback(row) : null"
                    :external-link="rowButton.externalLink"
                    :classes="`${rowButton.id}-button`"
                    :data-qa="`${rowButton.id}-button`"
                    :variant="rowButton.variant || 'tertiary'"
                    :icon="rowButton.icon"
                    :icon-props="{ color: 'shuttle-gray' }"
                    @focus="focusedRowId = row.id"
                    @blur="focusedRowId = null"
                    @button-click="rowButtonClicked(rowButton, row)"
                    :disabled="rowButton.disabledCallback ? rowButton.disabledCallback(row) : false"
                    :ignore-update-in-process="rowButton.ignoreUpdateInProcess"
                    :tooltip="
                      rowButton.tooltipCallback ? rowButton.tooltipCallback(row) : rowButton.tooltip
                    "
                    :lazy-tooltip="true"
                  />
                </div>
              </td>
            </template>
          </tr>
          <tr
            v-if="isCurrentExpandedOpen(row.id) || persistExpanderData"
            v-show="isCurrentExpandedOpen(row.id)"
            :class="['row-expanded-slot', { 'row-expanded': isCurrentExpandedOpen(row.id) }]"
          >
            <td :colspan="totalColumns">
              <slot name="rowExpander" :row="row" />
            </td>
          </tr>
        </template>
        <tr v-if="!slicedDataArrayDisplay?.length">
          <td :colspan="totalColumns" class="table-no-data-message" data-qa="no-data-message">
            <template v-if="$slots.tableNoDataOverride">
              <slot name="tableNoDataOverride" />
            </template>
            <div v-else>
              {{ t('table.noDataMessage') }}
            </div>
          </td>
        </tr>
        <tr
          v-if="showNewRow && showNewRowAtBottom"
          ref="additionRow"
          data-qa="addition-row"
          class="addition-row"
        >
          <td v-if="showExpanderColumn"></td>
          <td v-if="showCheckboxColumn"></td>
          <td
            v-if="!showExpanderColumn && !showCheckboxColumn && showFilterButton && showTopNavbar"
          ></td>
          <template v-if="$slots.newRowOverride">
            <slot name="newRowOverride" :data="newRowDataObject" :mode="`new`" />
          </template>
          <template v-else>
            <td v-for="col in columns" :key="col.title" :class="[col.classes]">
              <template v-if="col.overrideNewInput">
                <slot :name="col.overrideNewInput" :data="newRowDataObject" :mode="`new`" />
              </template>
              <template v-else>
                <form-input
                  :name="`new-row-${col.id}-input`"
                  :data-qa="`new-row-${col.id}-input`"
                  :placeholder="col.title"
                  :is-invalid="'validate' in col ? !col.validate(newRowDataObject) : false"
                  :error-message-text="
                    'errorMessage' in col ? col.errorMessage(newRowDataObject) : ''
                  "
                  v-model:inputValue="newRowDataObject[col.fieldNames[0]]"
                />
              </template>
            </td>
          </template>
          <template v-if="$slots.newRowOverrideConfirmationButtons">
            <slot name="newRowOverrideConfirmationButtons" :data="newRowDataObject" :mode="`new`" />
          </template>
          <template v-else>
            <td
              class="row-buttons-cell"
              :style="`--buttons-count:${buttonsCount}`"
              v-if="showButtonsColumn"
            >
              <form-confirmation
                @confirm="addInlineData"
                :is-invalid="isDataObjectInvalid(newRowDataObject)"
                @cancel="setShowNewRow(false)"
              />
            </td>
          </template>
        </tr>
      </tbody>
    </table>
    <div
      class="pagination-row"
      data-qa="table-pagination"
      v-if="rowsPerPage !== 'all' && dataArrayDisplay?.length > 20"
    >
      <div class="page-info">
        <div class="displayed-page-helper rbz-text-large" data-qa="displayed-page-helper">
          {{ t('table.pageOutOfTotalHelper', { current: currentPage, total: totalPages }) }}
        </div>
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="fun-blue" />
        </div>
        <div>
          <form-dropdown
            :items="overrideRowsPerPageOptions"
            v-model:value="overrideRowsPerPage"
            @update:value="currentPage = 1"
            chevron-color="stratos"
            :skip-sort="true"
            class="override-rows-per-page-dropdown"
          />
        </div>
      </div>
      <div v-if="totalPages > 1" class="page-number-buttons" data-qa="page-number-buttons">
        <template v-for="page in totalPages" :key="page">
          <button
            v-if="isPageNumberShown(page)"
            :class="['page-number-button', { active: page === currentPage }]"
            :data-qa="`page-button-${page}`"
            @click="navigatePage(page)"
          >
            {{ page }}
          </button>
          <button
            v-else-if="isPageNumberShown(page + 1)"
            class="page-number-button page-number-dots"
            data-qa="page-button-dots"
            disabled
          >
            ...
          </button>
        </template>
      </div>
      <div v-if="totalPages > 1" class="page-navigation-buttons" data-qa="page-navigation-buttons">
        <form-button
          variant="tertiary"
          classes="page-navigation-button"
          data-qa="previous-page-button"
          :label="t('table.previousPage')"
          :disabled="currentPage === 1"
          icon="IconChevron"
          icon-position="left"
          :icon-props="{ direction: 'left' }"
          @button-click="navigatePage(currentPage - 1)"
          :ignore-update-in-process="true"
        />
        <form-button
          variant="tertiary"
          classes="page-navigation-button"
          data-qa="next-page-button"
          :label="t('table.nextPage')"
          :disabled="currentPage === totalPages"
          icon="IconChevron"
          icon-position="right"
          :icon-props="{ direction: 'right' }"
          @button-click="navigatePage(currentPage + 1)"
          :ignore-update-in-process="true"
        />
      </div>
    </div>
    <slot name="customElement" :topNavbar="topNavbarRef" />
  </div>
</template>

<script setup lang="ts">
import { RbzTableColumnOptions, GenericObject, RbzTableRowButton } from '../../rbz/types';
import {
  computed,
  watch,
  ref,
  reactive,
  nextTick,
  onUnmounted,
  ComponentPublicInstance,
} from 'vue';
import FormButton from './FormButton.vue';
import FormCheckbox from './FormCheckbox.vue';
import FormConfirmation from './FormConfirmation.vue';
import FormInput from './FormInput.vue';
import LayoutDividerLine from './LayoutDividerLine.vue';
import _ from 'lodash';
import IconChevron from '../../assets/icons/IconChevron.vue';
import IconSortArrow from '../../assets/icons/IconSortArrow.vue';
import IconFilter from '../../assets/icons/IconFilter.vue';
import { useI18n } from 'vue-i18n';
import FormDropdown from '../../rbz/components/FormDropdown.vue';
import Utils from '../../helpers/Utils';

const { t } = useI18n();

export type RowsPerPage = '20' | '50' | '100' | '200' | 'all';

interface RbzTableProps {
  columns: RbzTableColumnOptions[] | null;
  tableData: GenericObject[] | null;
  defaultSortColumnDirection?: 'asc' | 'desc';
  defaultSortColumnIndex?: number;
  showTableResults?: boolean;
  showDownloadButton?: boolean;
  showNewButton?: boolean;
  newButtonLabel?: string;
  newButtonLink?: string;
  newButtonClasses?: string;
  showNewAsRow?: boolean;
  showNewRowAtBottom?: boolean;
  showNewDefaultData?: GenericObject;
  showFilterButton?: boolean;
  showButtonsColumn?: boolean;
  showTopNavbar?: boolean;
  stickyTopNavbar?: boolean;
  stickyHeader?: boolean;
  showCheckboxColumn?: boolean;
  showDeleteSelectedButton?: boolean;
  showExpanderColumn?: boolean;
  persistExpanderData?: boolean;
  numberOfResultsInColumn?: number;
  rowButtons?: RbzTableRowButton[];
  rowClasses?: (item: GenericObject) => string;
  rowsPerPage?: RowsPerPage;
  maxTableHeight?: string;
  dataQa?: string;
  showFilterRow?: boolean;
  hideHeaderRow?: boolean;
  showNarrowColumn?: boolean;
}

const props = withDefaults(defineProps<RbzTableProps>(), {
  defaultSortColumnDirection: 'asc',
  defaultSortColumnIndex: 0,
  showTableResults: true,
  showDownloadButton: true,
  showNewButton: true,
  showNewAsRow: false,
  showNewRowAtBottom: false,
  showFilterButton: true,
  showButtonsColumn: true,
  showTopNavbar: true,
  stickyTopNavbar: false,
  stickyHeader: false,
  rowsPerPage: '100',
  dataQa: '',
  showFilterRow: false,
  hideHeaderRow: false,
  showNarrowColumn: true,
});

interface RbzTableEmits {
  (e: 'new-button-clicked'): void;

  (e: 'add-inline-data', value: GenericObject): void;

  (e: 'edit-inline-data', value: GenericObject): void;

  (e: 'download-data'): void;

  (e: 'delete-selected', selected: string[]): void;

  (e: 'row-clicked', value: GenericObject): void;

  (e: 'edit-row-button-clicked', value: GenericObject): void;

  (e: 'duplicate-row-button-clicked', value: GenericObject): void;

  (e: 'delete-row-button-clicked', value: GenericObject): void;

  (e: 'attach-row-button-clicked', value: GenericObject): void;

  (e: 'restore-row-button-clicked', value: GenericObject): void;

  (e: 'sliced-data-array-display', value: GenericObject[]): void;
}

const emit = defineEmits<RbzTableEmits>();

const showNewRow = ref(false);
const currentExpandedIds = ref([]);
const rowChecked = ref(false);
const newRowDataObject = ref<GenericObject>({});
const rowsInEditMode = ref<{ [key: string]: GenericObject }>({});
const focusedRowId = ref(null);
const additionRow = ref(null);
const dataArrayDisplay = ref([]);
const topNavbarRef = ref(null);
const tableRef = ref(null);

// Sticky
const tableHeaderStickyTop = computed(() => {
  let top = 0;
  if (props.showTopNavbar && props.stickyTopNavbar && topNavbarRef.value) {
    top += topNavbarRef.value?.clientHeight || 0;
    top += parseInt(
      window.getComputedStyle(topNavbarRef.value, null).getPropertyValue('border-bottom-width')
    );
  }
  // top += parseInt(window.getComputedStyle(rbzPageContentRef, null).getPropertyValue('padding-top'))
  return `${top || 0}px`;
});

// Sorting
const sortDirection = ref('asc');
const sortColumnTitle = ref(null as RbzTableColumnOptions['title']);

let sortColumnDisplayFunction:
  | RbzTableColumnOptions['displayFunction']
  | RbzTableColumnOptions['sortDisplayFunction'] = null;
let sortColumnIsNumber = false as RbzTableColumnOptions['isNumber'];

// Filtering
const filter = ref({} as GenericObject);
const filtersVisible = ref(props.showFilterRow);
const updateFilterInputs = () => {
  filter.value = {};
  props.columns.forEach((column) => {
    if (column.isSearchable) {
      filter.value[column.id] = '';
    }
  });
};

// Pagination
const currentPage = ref(1);
const overrideRowsPerPageOptions: [RowsPerPage, string][] = [
  ['20', t('table.showRows', { amount: '20' })],
  ['50', t('table.showRows', { amount: '50' })],
  ['100', t('table.showRows', { amount: '100' })],
  ['200', t('table.showRows', { amount: '200' })],
  ['all', t('table.showAll')],
];
const overrideRowsPerPage = ref<RowsPerPage>(props.rowsPerPage);
watch(
  () => props.rowsPerPage,
  (val, oldVal) => {
    if (!_.isEqual(val, oldVal)) {
      overrideRowsPerPage.value = val;
    }
  }
);

// checkboxes
const selectedArray = ref([] as string[]);

const numberOfResultsString = computed((): string => {
  const numberOfResults = dataArrayDisplay.value?.length || 0;
  return t('table.numberOfResults', { count: numberOfResults });
});

const buttonsCount = computed((): number => {
  const minimum = props.showNewAsRow ? 2 : 0;
  const rowButtonsCount = props.rowButtons?.length || 0;
  return Math.max(minimum, rowButtonsCount);
});

const updateDataArrayDisplay = () => {
  if (!props.tableData?.length || !Array.isArray(props.tableData)) {
    dataArrayDisplay.value = [];
  }
  const sortModifier = sortDirection.value === 'asc' ? 1 : -1;
  dataArrayDisplay.value =
    props.tableData
      ?.filter((item: GenericObject) => {
        const keys = Object.keys(filter.value);
        return _.reduce(
          keys,
          (match: boolean, key: string) => {
            let getFilterValue: RbzTableColumnOptions['displayFunction'];
            const filterColumn = props?.columns.find((column) => {
              return column.id === key;
            });
            if (filterColumn?.displayFunction) {
              getFilterValue = filterColumn.displayFunction;
            } else {
              getFilterValue = (item: GenericObject) => {
                return item[filterColumn?.fieldNames[0]]?.toString() || '';
              };
            }
            const filterValue = getFilterValue(item)?.toString().toLowerCase() || '';
            let filterRegex;
            try {
              filterRegex = new RegExp(filter.value[key], 'im');
            } catch (_err) {
              filterRegex = null;
            }
            return match && (!filterRegex || filterRegex.test(filterValue));
          },
          true
        );
      })
      .sort((a: GenericObject, b: GenericObject) => {
        let getSortValue: RbzTableColumnOptions['displayFunction'];
        if (sortColumnDisplayFunction) {
          getSortValue = sortColumnDisplayFunction;
        } else {
          getSortValue = (item: GenericObject) => {
            if (!props?.columns) {
              return '';
            }
            const sortColumn = props?.columns.find((column) => {
              return column.title === sortColumnTitle.value;
            });
            const defaultValue = sortColumnIsNumber ? 0 : '';
            return item[sortColumn?.fieldNames?.[0]] || item[sortColumn?.id] || defaultValue;
          };
        }
        let sortValueA = getSortValue(a);
        let sortValueB = getSortValue(b);
        if (!sortColumnIsNumber) {
          const sortValueALowerCase = sortValueA.toString().toLowerCase();
          const sortValueBLowerCase = sortValueB.toString().toLowerCase();
          // only ignore case if the values are different from one another
          if (!_.isEqual(sortValueALowerCase, sortValueBLowerCase)) {
            sortValueA = sortValueALowerCase;
            sortValueB = sortValueBLowerCase;
          }
        }
        if (sortValueA < sortValueB) {
          return -1 * sortModifier;
        }
        if (sortValueA > sortValueB) {
          return 1 * sortModifier;
        }
        return 0;
      }) || [];
};

watch(
  () => [props.tableData, props.columns],
  () => {
    updateDataArrayDisplay();
  },
  {
    deep: true,
  }
);

const slicedDataArrayDisplay = computed((): GenericObject[] => {
  if (!dataArrayDisplay.value?.length) {
    return [];
  }
  if (overrideRowsPerPage.value === 'all') {
    return dataArrayDisplay.value;
  }
  const sliceStart = Number(overrideRowsPerPage.value) * (currentPage?.value - 1);
  const sliceEnd = sliceStart + Number(overrideRowsPerPage.value);
  return dataArrayDisplay.value?.slice(sliceStart, sliceEnd) || [];
});

watch(
  slicedDataArrayDisplay,
  () => {
    emit('sliced-data-array-display', slicedDataArrayDisplay.value);
  },
  { deep: true }
);

const totalColumns = computed((): number => {
  return (
    props?.columns.length +
    (props.showCheckboxColumn ? 1 : 0) +
    (props.showExpanderColumn ? 1 : 0) +
    (!props.showExpanderColumn && !props.showCheckboxColumn && props.showFilterButton ? 1 : 0) +
    (props.showButtonsColumn ? 1 : 0)
  );
});

watch(
  () => props.columns,
  (val) => {
    if (val?.length) {
      let defaultSortColumn;
      if (props.defaultSortColumnIndex) {
        defaultSortColumn = val[props.defaultSortColumnIndex];
      } else {
        defaultSortColumn = _.find(val, (columnOptions) => {
          return columnOptions?.isSortable;
        });
      }
      sortColumnTitle.value = defaultSortColumn?.title || null;
      if (defaultSortColumn?.sortDisplayFunction) {
        sortColumnDisplayFunction = defaultSortColumn.sortDisplayFunction;
      } else if (defaultSortColumn?.displayFunction && !defaultSortColumn?.isSortByOriginalValue) {
        sortColumnDisplayFunction = defaultSortColumn.displayFunction;
      } else {
        sortColumnDisplayFunction = null;
      }
      sortColumnIsNumber = defaultSortColumn?.isNumber || false;
      if (props.defaultSortColumnDirection) {
        sortDirection.value = props.defaultSortColumnDirection;
      }
      filter.value = _.reduce(
        val,
        (acc, column) => {
          if (column.isSearchable) {
            acc[column.id] = '';
          }
          return acc;
        },
        {} as Record<string, string>
      );
      filtersVisible.value = props.showFilterRow;
      currentPage.value = 1;
      updateDataArrayDisplay();
      updateFilterInputs();
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

watch(slicedDataArrayDisplay, (val) => {
  const dataIdArrayDisplay = _.map(val, 'id');
  if (val?.length > 0 && selectedArray.value?.length > 0) {
    // filter out whatever is NOT in dataIdArrayDisplay(slicedDataArrayDisplay)
    selectedArray.value = _.filter(selectedArray.value, (selectedArrayItem) => {
      return _.includes(dataIdArrayDisplay, selectedArrayItem);
    });
  } else {
    selectedArray.value = [];
  }
});

watch(dataArrayDisplay, (val, oldVal) => {
  if (!_.isEqual(val, oldVal)) {
    currentPage.value = 1;
  }
});

const totalPages = computed((): number => {
  if (overrideRowsPerPage.value === 'all') {
    return 1;
  }
  return Math.ceil(dataArrayDisplay.value?.length / Number(overrideRowsPerPage.value)) || 1;
});

const isPageNumberShown = (page: number) => {
  return (
    page === 1 ||
    page === totalPages.value ||
    (page >= currentPage.value - 1 && page <= currentPage.value + 1)
  );
};

const navigatePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const setCurrentPageToLastPage = () => {
  currentPage.value = totalPages.value;
};

const isCurrentRowFocused = (rowId: string): boolean => {
  return focusedRowId.value === rowId;
};

const isCurrentCheckboxIsChecked = (checkboxId: string): boolean => {
  return _.some(selectedArray.value, (value) => {
    return checkboxId === value;
  });
};

const isCurrentExpandedOpen = (expandedId: string | number): boolean => {
  return _.some(currentExpandedIds.value, (id) => {
    return expandedId === id;
  });
};

const toggleExpander = (id: string | number) => {
  if (!isCurrentExpandedOpen(id)) {
    currentExpandedIds.value.push(id);
  } else {
    currentExpandedIds.value = _.filter(currentExpandedIds.value, (expandedId: number) => {
      return expandedId !== id;
    });
  }
};

const sortColumn = (column: RbzTableColumnOptions) => {
  if (!column.isSortable) {
    return;
  }
  if (column.title === sortColumnTitle.value) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortDirection.value = 'asc';
  }
  sortColumnTitle.value = column.title;

  if (column.sortDisplayFunction) {
    sortColumnDisplayFunction = column.sortDisplayFunction;
  } else if (column.displayFunction && !column.isSortByOriginalValue) {
    sortColumnDisplayFunction = column.displayFunction;
  } else {
    sortColumnDisplayFunction = null;
  }

  sortColumnIsNumber = column.isNumber;
  updateDataArrayDisplay();
};

const addInlineData = () => {
  emit('add-inline-data', _.cloneDeep(newRowDataObject.value));
  showNewRow.value = false;
  newRowDataObject.value = {};
};

const editInlineData = (row: GenericObject) => {
  emit('edit-inline-data', _.cloneDeep(rowsInEditMode.value[row.id]));
  delete rowsInEditMode.value[row.id];
};

const downloadData = () => {
  emit('download-data');
};

const deleteData = () => {
  emit('delete-selected', selectedArray.value);
};

const dataCellRefs = ref<Record<string, Element | ComponentPublicInstance | null>>({});
const overflowCellMap = reactive<Record<string, boolean>>({});
const cellResizeObservers: Record<string, ResizeObserver> = {};

const registerCellRef = (el: Element | ComponentPublicInstance | null, key: string) => {
  dataCellRefs.value[key] = el;

  if (cellResizeObservers[key]) {
    cellResizeObservers[key].disconnect();
    delete cellResizeObservers[key];
  }

  if (el) {
    const htmlEl = el as HTMLElement;
    const observer = new ResizeObserver(() => {
      overflowCellMap[key] = Utils.elementTreeOverflow(htmlEl);
    });
    observer.observe(htmlEl);
    cellResizeObservers[key] = observer;
    nextTick(() => {
      overflowCellMap[key] = Utils.elementTreeOverflow(htmlEl);
    });
  }
};

onUnmounted(() => {
  Object.values(cellResizeObservers).forEach((o) => o.disconnect());
});

const cellTooltip = (col: RbzTableColumnOptions, row: GenericObject): string => {
  if (col.tooltipFunction) {
    return col.tooltipFunction(row);
  }
  if (col.displayFunction) {
    return col.displayFunction(row)?.toString();
  }
  return row[col.fieldNames[0]];
};

const cellContentClasses = (col: RbzTableColumnOptions, row: GenericObject) => {
  let returnString = '';
  if (col.cellContentClasses) {
    returnString += ` ${col.cellContentClasses}`;
  }
  if (col.cellContentConditionalClasses) {
    returnString += ` ${col.cellContentConditionalClasses(row)}`;
  }
  return returnString;
};

const selectAll = (checked: boolean) => {
  const currentCheckboxes = _.map(slicedDataArrayDisplay.value, 'id');
  if (checked) {
    selectedArray.value = _.cloneDeep(currentCheckboxes);
  } else {
    selectedArray.value = [];
  }
};

const rowSelected = (id: string) => {
  const selectedIndex = selectedArray.value.findIndex((row) => row === id);
  if (selectedIndex === -1) {
    selectedArray.value.push(id);
  } else {
    selectedArray.value.splice(selectedIndex, 1);
  }
};

const newButtonClicked = () => {
  if (props.showNewAsRow) {
    setShowNewRow(!showNewRow.value);
  } else {
    emit('new-button-clicked');
  }
};

const rowButtonClicked = (rowButton: RbzTableRowButton, row: GenericObject) => {
  const emitEventName = `${rowButton.id}-row-button-clicked`;
  // @ts-expect-error - Dynamic event name based on button id
  emit(emitEventName, row);
  if (rowButton.editInline) {
    if (!rowsInEditMode.value[row.id]) {
      rowsInEditMode.value[row.id] = _.cloneDeep(row);
    } else {
      delete rowsInEditMode.value[row.id];
    }
  }
};

const isDataObjectInvalid = (dataObject: GenericObject) => {
  return _.some(props.columns, (column) => {
    return 'validate' in column && !column.validate(dataObject);
  });
};

const setShowNewRow = (value: boolean) => {
  showNewRow.value = value;
  if (props.showNewDefaultData) {
    newRowDataObject.value = _.cloneDeep(props.showNewDefaultData);
  } else {
    newRowDataObject.value = {};
  }
  if (showNewRow.value) {
    nextTick(() => {
      const focusableElements = additionRow.value?.querySelectorAll("[tabindex='0']");
      focusableElements?.[0]?.focus();
    });
  }
};

defineExpose({
  toggleExpander,
  setShowNewRow,
  rowsInEditMode,
  tableRef,
  setCurrentPageToLastPage,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/main';
@use '../../assets/styles/colors' as colors;

.rbz-table-wrapper {
  background-color: inherit;
}

.scrollable {
  border-collapse: separate;
  overflow: hidden auto;
}

.top-navbar {
  background-color: inherit;
  border-bottom: 1px solid map.get(colors.$colors, 'silver');
  display: flex;
  justify-content: space-between;
  z-index: 2;
}

:deep(.top-navbar .filter-table-data-button) {
  .rbz-button:hover,
  .rbz-button:focus,
  .rbz-button:active {
    .filter-inactive {
      fill: map.get(colors.$colors, 'white');
    }
  }
}

.rbz-table-head {
  background-color: inherit;
  z-index: 1;
}

.table-results-wrapper {
  color: map.get(colors.$colors, 'dusty-gray');
  padding: 16px;
}

.table-buttons {
  display: flex;
  margin: 7px 16px;

  .table-buttons-icons {
    display: flex;
    gap: 4px;
  }

  .vertical-divider {
    height: 20px;
    margin-left: 4px;
    margin-right: 16px;
    position: relative;
  }
}

.rbz-table {
  background-color: inherit;
  border-collapse: separate;
  margin-bottom: 0;
  table-layout: fixed;
  text-align: left;
  width: 100%;
}

.header-row {
  font-size: 12px;
}

:deep(.rbz-table),
:deep(.content .rbz-table) {
  th,
  td {
    border-bottom: 1px solid map.get(colors.$colors, 'silver');
    height: 48px;
    text-align: left;
  }

  th {
    padding: 14px 12px 10px;
  }

  td {
    padding: 15px 12px;
  }

  .data-cell {
    color: map.get(colors.$colors, 'shuttle-gray');
    font-size: 14px;
    line-height: 18px;
    padding: 3px;
    vertical-align: middle;

    .data-cell-content {
      padding: 12px 9px;
    }
  }

  .data-cell.clickable a {
    color: map.get(colors.$colors, 'fun-blue');
    cursor: pointer;
  }

  .data-cell.vertical-align-top {
    vertical-align: top;
  }

  // Remove empty span created by tooltips. All content should be inside td/th cells
  tr > span {
    display: none;
  }

  .narrow-cell {
    line-height: 0;
    vertical-align: middle;
    width: 32px;
  }

  .expander-cell {
    @extend .narrow-cell;

    padding: 12px 4px;
  }

  .checkbox-cell {
    @extend .narrow-cell;

    padding: 15px 8px;
  }

  .row-buttons-cell {
    padding: 8px;
    vertical-align: middle;

    // 8px * 2 padding from both sides
    // 32px per button + 8px divider between buttons -> 40px*buttons-8px
    width: calc(16px + (40px * var(--buttons-count)) - 8px);
  }

  .table-no-data-message {
    text-align: center;
  }
}

.row-expanded {
  background-color: map.get(colors.$colors, 'alabaster');
}

.row-expanded > td:first-child {
  border-left: 3px solid map.get(colors.$colors, 'cornflower-blue-light');
}

.row-expanded > .expander-cell {
  padding: 12px 4px 12px 1px;
}

.row-expanded-slot > td {
  padding: 16px 40px 16px 26px;
}

.rbz-table.filters-active th,
.content .rbz-table.filters-active th {
  border-bottom: 0;
}

.filters-row {
  .filter-icon-cell {
    padding: 0;
  }

  .filter-icon-wrapper {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: end;
    width: 100%;
  }

  .filter-cell {
    padding-bottom: 8px;
    padding-top: 8px;
  }
}

.header-cell {
  color: map.get(colors.$colors, 'bright-gray');
  font-weight: 500;
  vertical-align: middle;
}

.header-cell-content {
  align-items: center;
  display: flex;
  gap: 4px;
  height: 24px;
}

.addition-row {
  background-color: map.get(colors.$colors, 'hawkes-blue');
  box-sizing: border-box;

  td,
  ::slotted(td) {
    /* stylelint-disable-line selector-pseudo-class-no-unknown */
    padding: 8px 12px;
    vertical-align: middle;
  }
}

.inline-table-icon {
  background: transparent;
  border: 0;
  cursor: pointer;
}

.navbar-icon {
  background: transparent;
  border: 0;
  cursor: pointer;
}

.navbar-icon:active {
  background-color: map.get(colors.$colors, 'hawkes-blue');
  border-radius: 4px;
}

.row-buttons {
  gap: 8px;
  justify-content: flex-end;
}

:deep(.row-buttons .rbz-button.tertiary) {
  border: 0;
  height: 32px;
  padding: 4px;
  width: 32px;
}

.data-row {
  .row-buttons {
    opacity: 0;
  }

  &:hover,
  &.row-focused {
    background-color: map.get(colors.$colors, 'selago');

    .row-buttons {
      opacity: 1;
    }

    :deep(.tertiary) {
      background-color: transparent;
    }

    :deep(.tertiary .button-icon) {
      fill: map.get(colors.$colors, 'jewel');
    }

    :deep(.tertiary:hover) {
      background-color: rgba(map.get(colors.$colors, 'shuttle-gray'), 0.1);
    }
  }
}

.row-checked {
  background-color: map.get(colors.$colors, 'hawkes-blue');
}

.pagination-row {
  align-items: center;
  display: flex;
  gap: 20px;
  height: 68px;
  justify-content: space-between;
  padding: 12px 24px 16px;

  .page-info {
    align-items: center;
    color: map.get(colors.$colors, 'stratos');
    display: flex;
    flex-shrink: 0;
    gap: 16px;
    height: 24px;
  }

  .vertical-divider {
    height: 20px;
    position: relative;
  }
}

:deep(.override-rows-per-page-dropdown) {
  .dropdown-header {
    background-color: transparent;
    border-color: transparent;
    max-height: 36px;
    min-height: 36px;
    padding: 5px 11px;
  }

  .selected-value {
    color: map.get(colors.$colors, 'stratos');
  }

  .dropdown-list {
    border-top-width: 1px;
    margin-top: 4px;
    width: max-content;
  }

  .menu-list-item {
    height: 100%;
  }

  .item-title {
    white-space: normal;
    word-wrap: break-word;
  }
}

.page-number-buttons {
  display: flex;
  gap: 2px;
}

.page-number-button {
  @extend .rbz-text-medium;

  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: map.get(colors.$colors, 'stratos');
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  width: 40px;

  &.active {
    background: map.get(colors.$colors, 'white-ice');
    color: map.get(colors.$colors, 'jewel');
  }

  &:hover {
    background: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'white');
  }

  &:focus {
    outline: none;
  }

  &:focus:not(:active) {
    box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
  }
}

.page-number-dots {
  cursor: default;

  &:hover {
    background: rgba(map.get(colors.$colors, 'jewel'), 0.3);
  }
}

.page-navigation-buttons {
  display: flex;
  gap: 2px;
}

:deep(.page-navigation-button) {
  background-color: transparent;

  &:disabled {
    background-color: transparent;
  }
}
</style>
